/**
 * Mappers para convertir entre DTOs y modelos de dominio
 * Responsabilidad: Transformar datos de API a modelos de negocio y viceversa
 */

import {
  QuoteItemDto,
  QuotePayloadDto,
  QuoteResponseDto,
  QuoteOfferDto,
  PagedOffersBlockDto,
  QuoteOffersResponseDto,
  OfferDetailDto,
  WorkTypeDto,
  ScheduleOptionDto,
  QuoteSummaryDto
} from '@infra/dto/quote.dto';
import {
  QuoteItem,
  Quote,
  QuoteOffer,
  PagedOffers,
  QuoteOffers,
  OfferDetail,
  WorkType,
  ScheduleOption,
  QuoteSummary
} from '@domain/models/quote.model';

export class QuoteMapper {
  /**
   * Convierte un modelo de dominio Quote a DTO para enviar a la API
   */
  static toDto(quote: Quote): QuotePayloadDto {
    return {
      contractorId: quote.contractorId,
      address: quote.address,
      districtName: quote.districtName,
      lat: quote.location.lat,
      lng: quote.location.lng,
      workType: quote.workType.description,
      workTypeId: quote.workType.id || null,
      workName: quote.workName,
      quoteMode: quote.quoteMode,
      deliveryQuantity: quote.deliveryQuantity,
      scheduleOptionId: quote.schedule?.optionId || null,
      dateOfWork: quote.schedule?.dateOfWork || null,
      items: quote.items.map(item => QuoteItemMapper.toDto(item))
    };
  }

  /**
   * Convierte un DTO de respuesta de la API a modelo de dominio Quote
   */
  static toDomain(dto: QuoteResponseDto): Quote {
    return {
      id: dto.quoteId,
      contractorId: dto.contractorId,
      workName: dto.workName,
      address: '', // No viene en la respuesta básica
      location: { lat: 0, lng: 0 }, // No viene en la respuesta básica
      workType: { description: '' },
      quoteMode: null,
      deliveryQuantity: 0,
      items: [],
      createdAt: dto.createdAt ? new Date(dto.createdAt) : undefined,
      status: dto.status
    };
  }

  /**
   * Convierte QuoteOffersResponseDto a modelo de dominio QuoteOffers
   */
  static offersToDomain(dto: QuoteOffersResponseDto): QuoteOffers {
    return {
      completed: QuoteMapper.pagedOffersToDomain(dto.completed),
      incompleted: QuoteMapper.pagedOffersToDomain(dto.incompleted)
    };
  }

  /**
   * Convierte PagedOffersBlockDto a modelo de dominio PagedOffers
   */
  static pagedOffersToDomain(dto: PagedOffersBlockDto): PagedOffers {
    return {
      total: dto.total,
      page: dto.page,
      pageSize: dto.pageSize,
      items: dto.items.map(item => QuoteOfferMapper.toDomain(item))
    };
  }

  /**
   * Convierte OfferDetailDto a modelo de dominio OfferDetail
   */
  static offerDetailToDomain(dto: OfferDetailDto): OfferDetail {
    // Preservar el formato original de createdAt si viene como string "YYYY-MM-DD HH:mm:ss"
    // Solo convertir a Date si es necesario para el modelo de dominio
    let createdAtDate: Date;
    if (typeof dto.createdAt === 'string') {
      // Intentar parsear el formato "YYYY-MM-DD HH:mm:ss"
      const dateStr = dto.createdAt.trim();
      if (dateStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        // Formato SQL datetime: reemplazar espacio por T para parseo ISO
        createdAtDate = new Date(dateStr.replace(' ', 'T'));
      } else {
        createdAtDate = new Date(dateStr);
      }
    } else {
      createdAtDate = new Date(dto.createdAt);
    }
    
    return {
      id: dto.quoteOfferId,
      workName: dto.workName,
      supplierName: dto.supplierName,
      createdAt: createdAtDate,
      distance: dto.distance,
      workType: dto.workType,
      address: dto.address,
      deliveryQuantity: dto.deliveryQuantity,
      products: dto.productos.map(p => ({
        product: p.product,
        quantity: p.quantity,
        unitPrice: p.unitPrice,
        subtotal: p.subtotal,
        enabled: p.enable
      })),
      total: dto.total,
      supplierLocation: dto.supplierLat && dto.supplierLng ? {
        lat: dto.supplierLat,
        lng: dto.supplierLng,
        address: dto.supplierAddress
      } : undefined,
      status: dto.status // Preservar el status del DTO
    };
  }

  /**
   * Convierte QuoteSummaryDto a modelo de dominio QuoteSummary
   * El DTO viene con estructura { work: {...}, products: [...] }
   */
  static summaryToDomain(dto: QuoteSummaryDto): QuoteSummary {
    return {
      id: '', // El backend no devuelve quoteId en el summary
      workName: dto.work.name || '',
      workType: dto.work.type || '',
      address: dto.work.address || '',
      items: dto.products.map(product => ({
        product: product.product,
        brand: product.brand,
        quantity: product.quantity
      })),
      createdAt: dto.work.date ? new Date(dto.work.date) : new Date()
    };
  }
}

export class QuoteItemMapper {
  static toDto(item: QuoteItem): QuoteItemDto {
    return {
      product: item.product,
      brand: item.brand,
      quantity: item.quantity
    };
  }

  static toDomain(dto: QuoteItemDto): QuoteItem {
    return {
      product: dto.product,
      brand: dto.brand,
      quantity: dto.quantity
    };
  }
}

export class QuoteOfferMapper {
  static toDomain(dto: QuoteOfferDto): QuoteOffer {
    return {
      id: dto.quoteOfferId,
      supplierName: dto.supplierName,
      total: dto.total,
      distance: dto.distance,
      coverage: dto.quoteCoverage
    };
  }
}

export class WorkTypeMapper {
  static toDomain(dto: WorkTypeDto): WorkType {
    return {
      id: dto.workTypeId,
      description: dto.description,
      sortOrder: dto.sortOrder
    };
  }

  static toDomainList(dtos: WorkTypeDto[]): WorkType[] {
    return dtos.map(dto => WorkTypeMapper.toDomain(dto));
  }
}

export class ScheduleOptionMapper {
  static toDomain(dto: ScheduleOptionDto): ScheduleOption {
    return {
      id: dto.scheduleOptionId,
      description: dto.description,
      sortOrder: dto.sortOrder
    };
  }

  static toDomainList(dtos: ScheduleOptionDto[]): ScheduleOption[] {
    return dtos.map(dto => ScheduleOptionMapper.toDomain(dto));
  }
}

